import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { Locale } from '@blauturquesa/shared/locales';
import type { PublicIndexCard } from '@blauturquesa/shared/public-view';
import { AvatarButton } from '../../shell/AvatarButton';
import { Icon } from '../../components/Icon';
import { getPublicIndex } from './publicData';
import { CaptionTag, captionText, Photo, pick } from './bits';

/**
 * Home immersiva (§10.2): fora de la carcassa, sense capçalera ni barra; només sortides flotants.
 * Una portada per propietat publicada, de `publicIndex/home` (una lectura). El disseny definitiu, pendent.
 */
export function HomePage() {
  const { t, i18n } = useTranslation(['shell', 'public']);
  const locale = i18n.language as Locale;
  const [cards, setCards] = useState<PublicIndexCard[] | null>(null);

  useEffect(() => {
    // Sense xarxa o sense propietats: la home continua mostrant-se, amb la portada de benvinguda.
    getPublicIndex().then((doc) => setCards(doc?.cards ?? []), () => setCards([]));
  }, []);

  return (
    <div className="home">
      <div className="home-top">
        <span className="home-brand">{t('common:appName')}</span>
        <AvatarButton floating />
      </div>
      <div className="home-track" aria-label={t('home.label')}>
        {cards !== null && cards.length === 0 && (
          <section className="home-slide tone-1" aria-roledescription="slide">
            <div className="home-caption"><h1>{t('public:home.welcome')}</h1></div>
          </section>
        )}
        {(cards ?? []).map((c, i) => {
          const photo = c.homePhotos[0] ?? c.cover;
          const place = [c.location.municipality, c.location.island].filter(Boolean).join(' · ');
          return (
            <Link
              key={c.propertyId}
              to={`/p/${c.slug}`}
              className={`home-slide tone-${(i % 3) + 1}`}
              aria-roledescription="slide"
              // Avançament de la personalitat de cada casa (§10): el peu destacat amb el degradat de la seva skin.
              style={c.skinGradient ? ({
                '--bt-gradient-from': c.skinGradient.from, '--bt-gradient-to': c.skinGradient.to,
                '--bt-gradient-angle': `${c.skinGradient.angle}deg`, '--bt-on-gradient': c.skinGradient.on,
              } as CSSProperties) : undefined}
            >
              {photo && <Photo className="home-photo" photo={photo} sizes="100vw" locale={locale} source={c.sourceLocale} eager={i === 0} />}
              <div className="home-caption">
                {place && <span className="glass-tag">{place}</span>}
                <h1>{pick(c.title, locale, c.sourceLocale).text}</h1>
                <CaptionTag text={captionText(photo?.caption, { ...(c.priceFrom ? { price: c.priceFrom } : {}), modes: c.modes, locale, source: c.sourceLocale, t })} className="home-tag" />
              </div>
            </Link>
          );
        })}
      </div>
      <Link className="home-cta glass" to="/explora">
        <Icon name="compass" size={20} /> {t('home.explore')}
      </Link>
    </div>
  );
}
